import React, { useEffect, useContext } from 'react';
import '../../App.css';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import { CheckBox } from '../../tools/controls';
import SearchComputeTable from './search-compute-table';
import SearchComputeServiceTypesList from './search-compute-service-types-list';
import Scrollbox from '../../tools/scrollbox';
import SidePanel from '../../tools/side-panel';

// types

// types relating to events.
import {
  Event,
  EventType,
  EventTarget,
  InitiateComputeSearchParams,
} from '../../utils/events';
import { availabilityOptions } from '../../utils/common/constants';

// enum.
import { SidePanelPosition } from '../../tools/side-panel';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions (redux).
import {
  setAvailabilityType,
  setDescription,
  setFastScratch,
  setFilterMaximised,
  setGPU,
  setHardwareType,
  setHighMemory,
  setLargeScratch,
  setLatitude,
  setLongitude,
  setMiddlewareVersion,
  setNodeValue,
  setServiceType,
  setSiteValue,
} from './search-compute-slice';
import { Site } from '../../utils/types';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SearchCompute(props: { tabVisible: boolean }) {
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------
  const dispatch = useAppDispatch();

  // global state variables.
  const sG_LoggedInRefreshTrigger: number | undefined = useAppSelector(
    (state) => state.global.loggedInRefreshTrigger,
  );
  const sG_SiteCapabilitiesTokenObtained: boolean | undefined = useAppSelector(
    (state) => state.global.siteCapabilitiesTokenObtained,
  );

  // data state variables.
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);

  const sDescription: string = useAppSelector(
    (state) => state.searchCompute.sDescription,
  );
  const sGPU: boolean = useAppSelector((state) => state.searchCompute.sGPU);
  const sLargeScratch: boolean = useAppSelector(
    (state) => state.searchCompute.sLargeScratch,
  );
  const sHighMemory: boolean = useAppSelector((state) => state.searchCompute.sHighMemory);
  const sFastScratch: boolean = useAppSelector(
    (state) => state.searchCompute.sFastScratch,
  );
  const sLatitude: number | undefined = useAppSelector(
    (state) => state.searchCompute.sLatitude,
  );
  const sLongitude: number | undefined = useAppSelector(
    (state) => state.searchCompute.sLongitude,
  );
  const sNodeValue: string = useAppSelector((state) => state.searchCompute.sNodeValue);
  const sSiteValue: string = useAppSelector((state) => state.searchCompute.sSiteValue);
  const sHardwareType: string = useAppSelector(
    (state) => state.searchCompute.sHardwareType,
  );
  const sServiceType: string = useAppSelector(
    (state) => state.searchCompute.sServiceType,
  );
  const sAvailabilityType: string = useAppSelector(
    (state) => state.searchCompute.sAvailabilityType,
  );
  const sMiddlewareVersion: string = useAppSelector(
    (state) => state.searchCompute.sMiddlewareVersion,
  );
  const sFilterMaximised: boolean = useAppSelector(
    (state) => state.searchCompute.sFilterMaximised,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------
  const availability = availabilityOptions(t);
  //	-------------------------------------------------
  //
  //	returns the users current latitude.
  //	only works if the users browser is set to allow location
  //	sharing
  //
  //	-------------------------------------------------

  function currentLatitude() {
    function success(pos: any) {
      //There is a delay when getCurrentPosition is called that causes the default 'undefined' to be returned
      //To get round this I call setLatitude and then (for full safety) also return the value
      setLatitude(pos.coords.latitude);
      return pos.coords.latitude;
    }
    function error(error: any) {
      return undefined;
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(success, error, { timeout: 10000 });
    } else {
      return undefined;
    }
    return undefined;
  } // currentLatitude

  //	-------------------------------------------------
  //
  //	returns the users current longitude.
  //	only works if the users browser is set to allow location
  //	sharing
  //
  //	-------------------------------------------------

  function currentLongitude() {
    function success(pos: any) {
      //There is a delay when getCurrentPosition is called that causes the default 'undefined' to be returned
      //To get round this I call setLongitude and then (for full safety) also return the value
      setLongitude(pos.coords.longitude);
      return pos.coords.longitude;
    }
    function error(error: any) {
      return undefined;
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(success, error, { timeout: 10000 });
    } else {
      return undefined;
    }
    return undefined;
  } // currentLongitude

  //	------------------------------------------------------------
  //
  //	Get a list of nodes and sites.
  //
  //	------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Handler for changes to the input boxes.
  //
  //	------------------------------------------------------------

  const inputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.target;

    // update the state for any input boxes that have been changed.
    if (inputBox.name === 'description') dispatch(setDescription(inputBox.value));
    if (inputBox.name === 'middlewareVersion')
      dispatch(setMiddlewareVersion(inputBox.value));
    if (inputBox.name === 'latitude') {
      if (inputBox.value !== '')
        try {
          const val: number = Number(inputBox.value);
          dispatch(setLatitude(val));
        } catch {
          dispatch(setLatitude(undefined));
        }
      else dispatch(setLatitude(undefined));
    }
    if (inputBox.name === 'longitude') {
      if (inputBox.value !== '')
        try {
          const val: number = Number(inputBox.value);
          dispatch(setLongitude(val));
        } catch {
          dispatch(setLongitude(undefined));
        }
      else dispatch(setLongitude(undefined));
    }

    // update the state for any check boxes that have been changed.
    if (inputBox.name === 'search-compute-gpu') {
      dispatch(setGPU(inputBox.checked));
    }
    if (inputBox.name === 'search-compute-large-scratch') {
      dispatch(setLargeScratch(inputBox.checked));
    }
    if (inputBox.name === 'search-compute-high-memory') {
      dispatch(setHighMemory(inputBox.checked));
    }
    if (inputBox.name === 'search-compute-fast-scratch') {
      dispatch(setFastScratch(inputBox.checked));
    }
    //
  }; // inputHandler

  //	------------------------------------------------------------
  //
  //	Restrict input box entry to numbers only, plus . , + -
  //
  //	------------------------------------------------------------

  const numericKeysOnly = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      (event.charCode < 48 || event.charCode > 57) &&
      (event.charCode < 43 || event.charCode > 46)
    )
      event.preventDefault();
  }; // numericKeysOnly

  //	------------------------------------------------------------
  //
  //	Handler for select box and check box onChange event.
  //
  //	------------------------------------------------------------

  const onChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    //
    const inputBox = event.target;

    // update the state after a select component change.
    if (inputBox.name === 'lst-node') {
      dispatch(setNodeValue(inputBox.value));
    }
    if (inputBox.name === 'lst-site') {
      dispatch(setSiteValue(inputBox.value));
    }
    if (inputBox.name === 'lst-hardware-type') {
      dispatch(setHardwareType(inputBox.value));
    }
    if (inputBox.name === 'lst-service-type') {
      dispatch(setServiceType(inputBox.value));
    }
    if (inputBox.name === 'lst-availability-type') {
      dispatch(setAvailabilityType(inputBox.value));
    }
    //
  }; // onChangeHandler

  //	------------------------------------------------------------
  //
  //	Handler for changes to the radio buttons.
  //
  //	------------------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.currentTarget;

    // update the state for any input boxes that have been changed.
    if (inputBox.name === 'hardwareType') {
      dispatch(setHardwareType(inputBox.value));
    }
    if (inputBox.name === 'serviceType') {
      dispatch(setServiceType(inputBox.value));
    }
    if (inputBox.name === 'middleware') {
      dispatch(setMiddlewareVersion(inputBox.value));
    }
    if (inputBox.name === 'gpu') {
      dispatch(setGPU(inputBox.checked));
    }

    // if we click a search button on the search form then open a new results tab.
    if (inputBox.name === 'searchData') {
      const params: InitiateComputeSearchParams = {
        node: sNodeValue,
        site: sSiteValue,
        gpu: sGPU,
        largeScratch: sLargeScratch,
        highMemory: sHighMemory,
        fastScratch: sFastScratch,
        description: sDescription,
        hardwareType: sHardwareType,
        middlewareVersion: sMiddlewareVersion,
        serviceType: sServiceType,
        availabilityType: sAvailabilityType,
        latitude: sLatitude,
        longitude: sLongitude,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.SEARCH_COMPUTE_TABLE,
        eventType: EventType.INITIATE_COMPUTE_SEARCH,
        parameters: params,
      };
      dispatch(handleEvent({ event: newEvent }));
    }
    //
  }; // onClickHandler

  //	-------------------------------------------------
  //
  //	sets the latitude and longitude to the users current
  //	geolocation
  //
  //	-------------------------------------------------

  const onClickCurrentLocation = (event: React.MouseEvent<HTMLElement>) => {
    setCurrentLocation();
  }; // onClickCurrentLocation

  //	-------------------------------------------------
  //
  //	function that handles a change in the selected service type on the search form
  //
  //	-------------------------------------------------

  const serviceTypeOnChangeHandler = function (args: { serviceType: string }) {
    dispatch(setServiceType(args.serviceType));
  }; // serviceTypeOnChangeHandler

  //	-------------------------------------------------
  //
  //	sets the latitude and longitude to the users current geolocation.
  //	only works if the users browser is set to allow location sharing
  //
  //	-------------------------------------------------

  function setCurrentLocation() {
    function success(position: any) {
      dispatch(setLatitude(position.coords.latitude));
      dispatch(setLongitude(position.coords.longitude));
    }
    function error(error: any) {
      console.log('Unable to retrieve location');
      switch (error.code) {
        case error.PERMISSION_DENIED:
          console.log('geolocation error: permission denied');
          break;
        case error.POSITION_UNAVAILABLE:
          console.log('geolocation error: position unavailable');
          break;
        case error.TIMEOUT:
          console.log('geolocation error: timeout');
          break;
        default:
          console.log('unknown geolocation error');
      }
      dispatch(setLatitude(undefined));
      dispatch(setLongitude(undefined));
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(success, error, { timeout: 10000 });
    } else {
      console.log('No geolocation support');
    }
  } // setCurrentLocation

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <div
      style={{
        flex: '1 1',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      {/* the column containing filter boxes, etc. */}
      <Scrollbox
        direction="vertical"
        height="100%"
        flex="0 0 auto"
        flexDirection="column"
        padding="10px 8px 10px 8px"
      >
        <SidePanel
          position={SidePanelPosition.LEFT}
          title={t('New search')}
          redux={true}
          maximised={sFilterMaximised}
          setMaximised={setFilterMaximised}
          width={'310px'}
        >
          <div
            style={{
              padding: '8pt',
              width: '100%',
              gap: '5px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/*
            /// node
            */}
            <label id="search-compute-node-label" className="text-colour">
              Node:
            </label>
            <select
              aria-labelledby="search-compute-node-label"
              name="lst-node"
              className="listbox"
              multiple={false}
              value={sNodeValue}
              onChange={onChangeHandler}
              data-placeholder-shown={sNodeValue === '' ? 'T' : 'F'}
            >
              <option hidden value="">
                {t('Select node')}
              </option>
              <option label="All" value="all">
                {' '}
                All{' '}
              </option>
              {sD_sites
                .filter(
                  (itm, idx) =>
                    sD_sites.findIndex((fnd) => fnd.node === itm.node) === idx,
                )
                .map((item) => (
                  <option key={item.node} label={item.node} value={item.node}>
                    {' '}
                    {item.node}{' '}
                  </option>
                ))}
            </select>

            {/*
                /// site
                */}
            <label
              id="search-compute-site-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              Site:
            </label>
            <select
              name="lst-site"
              className="listbox"
              multiple={false}
              value={sSiteValue}
              onChange={onChangeHandler}
              data-placeholder-shown={sSiteValue === '' ? 'T' : 'F'}
              style={{ lineHeight: '100%' }}
            >
              <option hidden value="">
                {t('Select site')}
              </option>
              <option label="All" value="all">
                {' '}
                All{' '}
              </option>
              {sD_sites
                .filter(
                  (itm, idx) =>
                    itm.node === sNodeValue &&
                    sD_sites.findIndex((fnd) => fnd.site_uuid === itm.site_uuid) === idx,
                )
                .map((item) => (
                  <option key={item.site} label={item.site} value={item.site}>
                    {' '}
                    {item.site}{' '}
                  </option>
                ))}
            </select>

            {/*
                /// description
                */}
            <label
              id="search-compute-description-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              Description:
            </label>
            <input
              aria-labelledby="search-compute-description-label"
              className="input-field"
              type="text"
              name="description"
              placeholder={t('Description')}
              onChange={inputHandler}
              maxLength={50}
              value={sDescription}
            ></input>

            {/*
            /// hardware capabilities
            */}
            <label
              id="search-compute-hardware-capabilities-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              {t('Hardware capabilities')}:
            </label>
            <div
              role="group"
              aria-labelledby="search-compute-hardware-capabilities-label"
              className="container"
            >
              <div className="container-row">
                <div className="container-cell">
                  <CheckBox
                    key={t('GPU') + (props.tabVisible ? 't' : 'f')}
                    name="search-compute-gpu"
                    value="GPU"
                    text={t('GPU')}
                    checked={sGPU === true}
                    onChange={inputHandler}
                  />
                </div>
                <div className="container-cell">
                  <CheckBox
                    key={t('High memory') + (props.tabVisible ? 't' : 'f')}
                    name="search-compute-high-memory"
                    value="highMemory"
                    text={t('High memory')}
                    checked={sHighMemory === true}
                    onChange={inputHandler}
                  />
                </div>
              </div>
              <div className="container-row">
                <div className="container-cell">
                  <CheckBox
                    key={t('Large scratch') + (props.tabVisible ? 't' : 'f')}
                    name="search-compute-large-scratch"
                    value="largeScratch"
                    text={t('Large scratch')}
                    checked={sLargeScratch === true}
                    onChange={inputHandler}
                  />
                </div>
                <div className="container-cell">
                  <CheckBox
                    key={t('Fast scratch') + (props.tabVisible ? 't' : 'f')}
                    name="search-compute-fast-scratch"
                    value="fastScratch"
                    text={t('Fast scratch')}
                    checked={sFastScratch === true}
                    onChange={inputHandler}
                  />
                </div>
              </div>
            </div>

            {/*
            /// hardware type
            */}
            <label
              id="search-compute-hardware-type-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              {t('Hardware type')}:
            </label>
            <select
              aria-labelledby="search-compute-hardware-type-label"
              name="lst-hardware-type"
              className="listbox"
              multiple={false}
              value={sHardwareType}
              onChange={onChangeHandler}
              data-placeholder-shown={sHardwareType === '' ? 'T' : 'F'}
              style={{
                lineHeight: '100%',
              }}
            >
              <option hidden value="">
                {t('Select hardware type')}
              </option>
              <option label={t('Any')} value="any">
                {' '}
                Any{' '}
              </option>
              <option label={t('Container')} value="container">
                {' '}
                Container{' '}
              </option>
              <option label={t('Bare metal')} value="bare_metal">
                {' '}
                Bare Metal{' '}
              </option>
              <option label={t('Virtual machine')} value="vm">
                {' '}
                Virtual Machine{' '}
              </option>
            </select>

            {/*
            /// availability type
            */}
            <label
              id="search-compute-availability-type-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              {t('Availability type')}:
            </label>
            <select
              aria-labelledby="search-compute-availability-type-label"
              name="lst-availability-type"
              className="listbox"
              multiple={false}
              value={sAvailabilityType}
              onChange={onChangeHandler}
              data-placeholder-shown={sAvailabilityType === '' ? 'T' : 'F'}
              style={{ lineHeight: '100%' }}
            >
              <option hidden value="">
                {t('Select availability type')}
              </option>
              {availability.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            {/*
            /// service type
            */}
            <label
              id="search-compute-service-type-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              {t('Service type')}:
            </label>
            <SearchComputeServiceTypesList
              key={sG_LoggedInRefreshTrigger}
              changeServiceType={serviceTypeOnChangeHandler}
              placeholderShown={sServiceType === ''}
              value={sServiceType}
            />

            <label className="header-text" style={{ marginTop: '5px' }}>
              {t('Sort sites by distance')}
            </label>

            {/*
            /// latitude
            */}
            <label
              id="search-compute-latitude-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              {t('Latitude')}:
            </label>
            <div className="latlon-input">
              <input
                aria-labelledby="search-compute-latitude-label"
                name="latitude"
                className="input-field"
                type="text"
                placeholder={t('Latitude')}
                step="0.1"
                min="-90.0"
                max="90.0"
                onKeyPress={numericKeysOnly}
                onChange={inputHandler}
                maxLength={20}
                value={sLatitude}
              />
              <span className="text-colour">&deg;N</span>
            </div>

            {/*
            /// longitude
            */}
            <label
              id="search-compute-longitude-label"
              className="text-colour"
              style={{
                marginTop: '5px',
              }}
            >
              {t('Longitude')}:
            </label>
            <div className="latlon-input">
              <input
                aria-labelledby="search-compute-longitude-label"
                name="longitude"
                className="input-field"
                type="text"
                placeholder={t('Longitude')}
                step="0.1"
                min="-180.0"
                max="180.0"
                onKeyPress={numericKeysOnly}
                onChange={inputHandler}
                maxLength={20}
                value={sLongitude}
              />
              <span className="text-colour">&deg;E</span>
            </div>
          </div>

          <div style={{ margin: '15px 10px 10px 10px' }}>
            <ToolButton
              key={t('Search') + (props.tabVisible ? 't' : 'f')}
              name="searchData"
              onClick={onClickHandler}
              text={t('Search')}
              type={ToolButtonType.PRIMARY}
            />
          </div>
        </SidePanel>
      </Scrollbox>

      {/*
       //
       //	the SearchComputeTable component runs a search query with the given parameters, and displays the results in a table.
       //
       */}
      <div className="search-compute-table">
        <div style={{ flex: '0 0 10px' }} />
        <SearchComputeTable />
        <div style={{ flex: '0 0 10px' }} />
      </div>
      <div style={{ flex: '0 0 10px' }} />
    </div>
  );
} // SearchCompute
